import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { approveLiquidationRequestManual } from "../../redux/slice/liquidationSlice";
import {Form, Input, Upload, Button, Spin, Select} from "antd";
import {message} from "../../utils/antdFeedback";
import { UploadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Landmark } from "lucide-react";
import {adminRequest, getReturnedObject} from "../../api/adminApi";
import { HeroHeader, Card, COLORS } from "../../designSystem";

function ManualApproveLiquidation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const { token, id: userId } = useSelector((state: RootState) => state.account);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [approving, setApproving] = useState(false);
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [banks, setBanks] = useState<any[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const [form] = Form.useForm();

  // 🔹 Fetch liquidation request details
  useEffect(() => {
    if (!token || !id) return;

    setLoading(true);
    adminRequest(`/group-savings/liquidation-request/${id}`, {token})
      .then((json) => setData(getReturnedObject(json)))
      .catch(() => message.error("Failed to load request details"))
      .finally(() => setLoading(false));
  }, [token, id]);

  // 🔹 Fetch banks (only once)
  useEffect(() => {
    if (!token) return;

    setLoadingBanks(true);
    adminRequest("/payment/vfd-banks", {token})
      .then((json) => setBanks(getReturnedObject(json) || []))
      .catch(() => message.error("Could not fetch bank list"))
      .finally(() => setLoadingBanks(false));
  }, [token]);

  // 🔹 Upload proof
  const handleUpload = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const json = await adminRequest("/file/document", {
        method: "POST",
        token,
        body: formData,
      });

      if (json.isSuccessful && json.returnedObject?.fileUrl) {
        setProofUrl(json.returnedObject.fileUrl);
        form.setFieldsValue({ proofUrl: json.returnedObject.fileUrl });
        message.success("Payment proof uploaded.");
      } else {
        message.error(json.message || "Upload failed.");
      }
    } catch {
      message.error("Upload failed.");
    }
    return false;
  };

  // 🔹 Submit manual approval
  const handleApprove = (values: any) => {
    if (!data || !userId || !proofUrl) {
      message.error("Payment proof is required.");
      return;
    }

    setApproving(true);

    dispatch(
      approveLiquidationRequestManual({
        token,
        userId,
        savingsId: data.entityId,
        requestId: data.id,
        proofUrl,
        accountName: values.accountName,
        accountNumber: values.accountNumber,
        bankCode: values.bankCode,
        bankName: values.bankName,
        externalReference: values.externalReference,
        narration: values.narration,
      })
    )
      .unwrap()
      .then(() => {
        message.success("Manual liquidation recorded successfully.");
        navigate("/requests");
      })
      .catch((err) => {
        message.error(err || "Manual approval failed.");
      })
      .finally(() => setApproving(false));
  };

  // 🔹 Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="m-10">
          <Spin tip="Loading request details..." />
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="m-10">
          <p>Unable to load liquidation request.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{ background: COLORS.surface }}>
        <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center font-medium bg-transparent border-none cursor-pointer transition-all"
          style={{ color: COLORS.brand }}
        >
          <ArrowLeftOutlined className="mr-2" /> Back
        </button>

        <HeroHeader
          icon={Landmark}
          title="Manual Approve Liquidation Request"
          subtitle="Record a manual payout with proof of payment."
        />

        <Card className="mt-8 p-8 space-y-6">
          {/* Request details */}
          <div className="grid grid-cols-2 gap-4 text-sm" style={{ color: COLORS.text }}>
            <div>
              <span className="font-semibold block">Requested By:</span>
              <span>{data.requestedBy?.fullName}</span>
            </div>
            <div>
              <span className="font-semibold block">Email:</span>
              <span>{data.requestedBy?.email}</span>
            </div>
            <div>
              <span className="font-semibold block">Amount:</span>
              <span className="font-mono">{`₦${parseFloat(data.amount).toLocaleString()}`}</span>
            </div>
            <div>
              <span className="font-semibold block">Group:</span>
              <span>{data.group?.name}</span>
            </div>
            <div>
              <span className="font-semibold block">Request Date:</span>
              <span>{new Date(data.dateCreated).toLocaleString()}</span>
            </div>
            <div>
              <span className="font-semibold block">Status:</span>
              <span>{getStatusLabel(data.status)}</span>
            </div>
          </div>

          {/* Manual approval form */}
          <Form layout="vertical" form={form} onFinish={handleApprove}>
            <Form.Item label="Account Name" name="accountName" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item
            label="Account Number"
            name="accountNumber"
            rules={[
                { required: true, message: "Account number is required" },
                { pattern: /^\d{10}$/, message: "Account number must be exactly 10 digits" },
            ]}
            >
            <Input
                maxLength={10}
                onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                    e.preventDefault(); // block non-numeric input
                }
                }}
            />
            </Form.Item>

           <Form.Item
            label="Bank"
            name="bankName"
            rules={[{ required: true, message: "Please select a bank" }]}
            >
            <Select
                loading={loadingBanks}
                showSearch
                placeholder="Select a bank"
                optionFilterProp="label" // ✅ use label for searching
                filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value: string, option: any) => {
                form.setFieldsValue({ bankCode: option.bankcode });
                }}
            >
                {banks.map((bank) => (
                <Select.Option
                    key={bank.id}
                    value={bank.name}
                    bankcode={bank.code}
                    label={`${bank.name} ${bank.code}`}
                >
                    <div className="flex items-center gap-2">
                    {bank.logo && bank.logo.trim() !== "" && (
                        <img src={bank.logo} alt={bank.name} className="w-5 h-5 rounded" />
                    )}
                    <span>{bank.name}</span>
                    <span className="text-gray-500 text-xs">({bank.code})</span>
                    </div>
                </Select.Option>
                ))}
            </Select>
            </Form.Item>
            
            <Form.Item label="Bank Code" name="bankCode" rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>

            <Form.Item label="External Reference" name="externalReference">
              <Input />
            </Form.Item>

            <Form.Item label="Narration" name="narration">
              <Input.TextArea rows={3} />
            </Form.Item>

           <Form.Item
            name="proofUrl"
            rules={[{ required: true, message: "Payment proof is required" }]}
            hidden
            >
            <Input />
            </Form.Item>

            <Form.Item label="Upload Payment Proof" required>
            <Upload beforeUpload={handleUpload} showUploadList={false}>
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
            {proofUrl && (
                <p className="text-green-600 mt-2 break-all">
                Uploaded:{" "}
                <a
                    href={proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600 hover:text-blue-800"
                >
                    View Payment Proof
                </a>
                </p>
            )}
            </Form.Item>

           <Button
            type="primary"
            htmlType="submit"
            loading={approving}
            disabled={
                approving ||
                !form.isFieldsTouched(true) ||
                form.getFieldsError().some(({ errors }) => errors.length) ||
                !proofUrl
            }
            className="bg-orange-600 hover:bg-orange-700 border-none outline-none"
            >
            {approving ? "Approving..." : "Submit Manual Approval"}
            </Button>

          </Form>
        </Card>
        </div>
      </main>
    </AdminLayout>
  );
}

function getStatusLabel(status: number): string {
  switch (status) {
    case 1:
      return "Processing";
    case 2:
      return "Completed";
    case 3:
      return "Pending Approval";
    default:
      return "Unknown";
  }
}

export default ManualApproveLiquidation;
